import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  ClipboardCheck,
  Camera,
  PenTool,
  FileText,
  WifiOff,
  ShieldCheck,
  BarChart3,
  Users,
  Plane,
  MoveRight,
  CircleDollarSign,
  FileSpreadsheet,
  Landmark,
  AlertTriangle,
  CheckCircle2,
  Mail,
  LogIn,
  Download,
} from "lucide-react";

const DEMO_MAILTO =
  "mailto:brito@service-engenharia.com?subject=Demonstra%C3%A7%C3%A3o%20AeroGestor&body=Ol%C3%A1%2C%20gostaria%20de%20agendar%20uma%20demonstra%C3%A7%C3%A3o%20do%20AeroGestor%20para%20meu%20hangar.";

/* ---------------------------------- NAV ---------------------------------- */
function Nav() {
  return (
    <header className="fixed top-0 inset-x-0 z-50 border-b border-white/10 bg-[#0A1726]/85 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <a href="#topo" className="flex items-center gap-3" data-testid="landing-nav-logo">
          <img src="/aerogestor-symbol.jpg" alt="AeroGestor" className="w-9 h-9 rounded-sm object-cover ring-1 ring-white/25" />
          <span className="font-display font-black text-xl tracking-tight ag-brand-metallic">AeroGestor</span>
        </a>
        <nav className="hidden md:flex items-center gap-8 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">
          <a href="#fluxo" className="hover:text-sky-300 transition-colors" data-testid="landing-nav-fluxo">Como funciona</a>
          <a href="#funcionalidades" className="hover:text-sky-300 transition-colors" data-testid="landing-nav-funcionalidades">Funcionalidades</a>
          <a href="#perfis" className="hover:text-sky-300 transition-colors" data-testid="landing-nav-perfis">Perfis</a>
          <a href="#guia" className="hover:text-sky-300 transition-colors" data-testid="landing-nav-guia">Na prática</a>
          <a href="#contato" className="hover:text-sky-300 transition-colors" data-testid="landing-nav-contato">Contato</a>
        </nav>
        <Link to="/login" data-testid="landing-nav-login">
          <Button className="h-9 px-4 rounded-sm bg-[#0052CC] hover:bg-[#003D99] text-white border border-white/20 text-xs font-bold tracking-wider uppercase">
            <LogIn className="w-3.5 h-3.5 mr-2" /> Entrar
          </Button>
        </Link>
      </div>
    </header>
  );
}

/* ---------------------------------- HERO --------------------------------- */
function Hero() {
  return (
    <section id="topo" className="relative min-h-[92vh] flex items-center overflow-hidden">
      <img src="/aerogestor-logo.jpg" alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A1726]/95 via-[#0A1726]/80 to-[#0A1726]/40" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A1726] via-transparent to-[#0A1726]/60" />
      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full pt-28 pb-20">
        <div className="max-w-3xl ag-fade-up">
          <div className="inline-flex items-center gap-2 border border-white/20 rounded-sm px-3 py-1.5 mb-8 font-mono text-[10px] uppercase tracking-[0.22em] text-sky-300">
            <span className="w-1.5 h-1.5 rounded-full bg-sky-300 animate-pulse" />
            Sistema Operacional de Hangares
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tighter text-white">
            Do pouso ao faturamento.
            <br />
            <span className="text-sky-300">Rastreabilidade total</span>
            <br />
            da rampa ao back-office.
          </h1>
          <p className="mt-8 text-slate-300 max-w-xl text-base leading-relaxed">
            O AeroGestor registra cada etapa da estadia de uma aeronave no seu hangar:
            checklist de recepção com fotos, serviços de solo, movimentações no pátio,
            assinatura digital do piloto, precificação e nota de serviço — tudo em um
            único sistema, com relatórios ANAC prontos.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link to="/login" data-testid="landing-hero-cta-login">
              <Button className="h-13 px-8 py-4 h-auto rounded-sm bg-[#0052CC] hover:bg-[#003D99] text-white border border-white/20 font-bold text-sm tracking-wide">
                Acessar o sistema <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <a href={DEMO_MAILTO} data-testid="landing-hero-cta-demo">
              <Button variant="outline" className="h-13 px-8 py-4 h-auto rounded-sm bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-sky-300 font-bold text-sm tracking-wide">
                <Mail className="w-4 h-4 mr-2" /> Solicitar demonstração
              </Button>
            </a>
          </div>
          <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-xl">
            {[
              ["4", "Estados de OS"],
              ["3", "Perfis de acesso"],
              ["ANAC", "Relatório pronto"],
              ["100%", "Offline na rampa"],
            ].map(([v, l]) => (
              <div key={l} className="border-l-2 border-sky-300/40 pl-3">
                <div className="font-display font-black text-2xl text-white">{v}</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 mt-1">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------- PROBLEMA ------------------------------- */
const PAINS = [
  {
    icon: FileSpreadsheet,
    title: "Planilhas e pranchetas",
    text: "Registros manuais se perdem entre a rampa e o escritório. Sem histórico confiável, cada auditoria vira um mutirão.",
  },
  {
    icon: CircleDollarSign,
    title: "Serviços não faturados",
    text: "Rebocamento, GPU, pernoite extra… serviços executados no pátio que nunca chegam à nota. Receita evaporando todo mês.",
  },
  {
    icon: Camera,
    title: "Sem evidência",
    text: "Uma avaria contestada pelo cliente sem foto de intake é prejuízo garantido. Sem prova, o hangar sempre perde.",
  },
  {
    icon: Landmark,
    title: "Relatório ANAC na mão",
    text: "Compilar movimentações e permanências manualmente consome dias e ainda expõe a empresa a erros regulatórios.",
  },
];

function Problem() {
  return (
    <section className="relative py-24 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-sky-300 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-3.5 h-3.5" /> O problema
            </div>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter text-white leading-[1.05]">
              Quanto o seu hangar perde sem rastreabilidade?
            </h2>
            <p className="mt-6 text-slate-400 text-base leading-relaxed">
              Operações de ground handling e hangaragem movimentam dezenas de aeronaves
              por semana. Cada registro perdido entre a rampa e o back-office é receita
              que não volta — e risco regulatório que se acumula.
            </p>
            <div className="mt-8 rounded-sm overflow-hidden border border-white/10">
              <img
                src="/demo-passado.jpg"
                alt="O passado: faturamentos baseados na memória — AeroGestor: precisão, segurança e integração em tempo real"
                className="w-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {PAINS.map(({ icon: Icon, title, text }) => (
              <div key={title} className="rounded-sm border border-white/10 bg-[#132A42]/60 backdrop-blur-xl p-6 hover:border-sky-300/30 transition-colors">
                <Icon className="w-6 h-6 text-sky-300 mb-4" strokeWidth={1.5} />
                <h3 className="font-display font-black text-lg tracking-tight text-white mb-2">{title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------ FLUXO PASSO A PASSO ---------------------------- */
const STEPS = [
  {
    num: "01",
    icon: ClipboardCheck,
    image: "/demo-novo-atendimento.jpg",
    alt: "Operador abrindo novo atendimento no tablet dentro do hangar",
    title: "Abrindo novo atendimento",
    text: "A aeronave estaciona e o operador clica em ABRIR NOVO ATENDIMENTO direto do tablet. Preenchimento automático: prefixo, modelo e cliente aparecem instantaneamente a partir dos cadastros. Origem, destino, horários de chegada e saída prevista e código ANAC — tudo em segundos.",
    tags: ["PREENCHIMENTO AUTOMÁTICO", "LOGIN INDIVIDUAL", "OS SEQUENCIAL"],
  },
  {
    num: "02",
    icon: CheckCircle2,
    image: "/demo-checklist.jpg",
    alt: "Checklist de recebimento com inspeção visual no tablet",
    title: "Checklist de recebimento",
    text: "Inspeção visual obrigatória: para-brisa, exterior, pneus, luzes e combustível. Encontrou irregularidade? Marque o X vermelho, descreva a observação e anexe fotos do recebimento na hora. O checklist não é burocracia — é o respaldo técnico do hangar contra reclamações indevidas.",
    tags: ["INSPEÇÃO OBRIGATÓRIA", "FOTOS DE EVIDÊNCIA", "STATUS: EM OPERAÇÃO"],
  },
  {
    num: "03",
    icon: MoveRight,
    image: "/demo-servicos.jpg",
    alt: "Aba de serviços executados no tablet",
    title: "Registro de serviços executados",
    text: "Na aba SERVIÇOS EXECUTADOS, o operador seleciona os itens — lavagem, reboque, GPU, abastecimento — na hora em que acontecem. Sem preços visíveis: o único foco é a quantidade e a precisão da execução. O escritório cuida dos valores depois.",
    tags: ["CATÁLOGO DE SERVIÇOS", "SEM PREÇOS P/ OPERADOR", "REGISTRO NA HORA"],
  },
  {
    num: "04",
    icon: Plane,
    image: "/demo-movimentacao.jpg",
    alt: "Registro de movimentação da aeronave no tablet",
    title: "Registro de movimentação",
    text: "A aeronave saiu e vai voltar? Saída para pista de táxi ou reboque externo? Registre a movimentação com data, hora, tipo e observação do motivo — e o histórico de custódia e faturamento permanece íntegro do início ao fim da estadia.",
    tags: ["SAÍDA TEMPORÁRIA", "HISTÓRICO DE CUSTÓDIA", "DATA E HORA"],
  },
  {
    num: "05",
    icon: PenTool,
    image: "/demo-finalizacao.jpg",
    alt: "Tela de finalização com assinaturas digitais no tablet",
    title: "Finalização e envio para faturamento",
    text: "O passo de ouro: capture a assinatura manuscrita do cliente diretamente na tela, registre a aeronave pronta para entrega com as fotos finais e clique em FINALIZAR E ENVIAR PARA FATURAMENTO. A OS trava para edição e chega instantaneamente ao escritório.",
    tags: ["ASSINATURA NA TELA", "FOTOS FINAIS", "TRAVA DE EDIÇÃO"],
  },
];

function Flow() {
  return (
    <section id="fluxo" className="relative py-24 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-sky-300 mb-4">Como funciona</div>
        <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter text-white leading-[1.05] max-w-2xl">
          O aplicativo na prática, passo a passo.
        </h2>
        <p className="mt-5 text-slate-400 max-w-2xl text-base leading-relaxed">
          Cada Ordem de Serviço percorre uma máquina de estados rígida — Em Operação →
          Em Andamento → Aguardando Faturamento → Faturada. Veja como o operador conduz
          o atendimento do pouso à entrega, direto do tablet.
        </p>
        <div className="mt-16 space-y-16">
          {STEPS.map(({ num, icon: Icon, image, alt, title, text, tags }, i) => (
            <div key={num} className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
              <div className={`lg:col-span-7 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <div className="rounded-sm overflow-hidden border border-white/10 shadow-2xl shadow-black/40">
                  <img src={image} alt={alt} className="w-full object-cover" loading="lazy" />
                </div>
              </div>
              <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-sm bg-[#0052CC]/25 border border-sky-300/30 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-sky-300" strokeWidth={1.5} />
                  </div>
                  <span className="font-mono text-xs text-sky-300 tracking-[0.2em]">PASSO {num}</span>
                </div>
                <h3 className="font-display font-black text-2xl tracking-tight text-white mb-3">{title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{text}</p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {tags.map((t) => (
                    <span key={t} className="font-mono text-[9px] tracking-[0.15em] text-slate-300 border border-white/15 rounded-sm px-2 py-1">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------------------- FUNCIONALIDADES ----------------------------- */
function FeatureCard({ icon: Icon, label, title, text, className = "" }) {
  return (
    <div className={`rounded-sm border border-white/10 bg-[#132A42]/60 backdrop-blur-xl p-7 hover:border-sky-300/30 transition-colors ${className}`}>
      <div className="flex items-center justify-between mb-5">
        <Icon className="w-6 h-6 text-sky-300" strokeWidth={1.5} />
        <span className="font-mono text-[9px] tracking-[0.2em] text-slate-500 uppercase">{label}</span>
      </div>
      <h3 className="font-display font-black text-lg tracking-tight text-white mb-2">{title}</h3>
      <p className="text-sm text-slate-400 leading-relaxed">{text}</p>
    </div>
  );
}

function Features() {
  return (
    <section id="funcionalidades" className="relative py-24 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-sky-300 mb-4">Funcionalidades</div>
        <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter text-white leading-[1.05] max-w-2xl">
          Construído para a realidade da rampa — e do escritório.
        </h2>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-4">
          {/* Offline — destaque grande */}
          <div className="md:col-span-7 rounded-sm border border-sky-300/25 bg-[#0052CC]/15 backdrop-blur-xl p-8 relative overflow-hidden">
            <div className="absolute top-4 right-5 font-mono text-[9px] tracking-[0.2em] text-sky-300 border border-sky-300/40 rounded-sm px-2 py-1">
              STATUS: OFFLINE SYNC
            </div>
            <WifiOff className="w-7 h-7 text-sky-300 mb-5" strokeWidth={1.5} />
            <h3 className="font-display font-black text-2xl tracking-tight text-white mb-3">Modo offline na rampa</h3>
            <p className="text-sm text-slate-300 leading-relaxed max-w-lg">
              Pátio sem sinal? O AeroGestor continua operando. Checklists, serviços e
              movimentações ficam em fila local no dispositivo e sincronizam automaticamente
              assim que a conexão volta. O operador nunca para — e nenhum registro se perde.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {["SERVICE WORKER", "FILA DE SINCRONIZAÇÃO", "BANNER DE STATUS"].map((t) => (
                <span key={t} className="font-mono text-[9px] tracking-[0.15em] text-sky-200 border border-sky-300/25 rounded-sm px-2 py-1">{t}</span>
              ))}
            </div>
          </div>
          <FeatureCard
            className="md:col-span-5"
            icon={ShieldCheck}
            label="RBAC: 3 PERFIS"
            title="Controle de acesso por perfil"
            text="Admin, Operador e Escritório — cada um vê apenas o que precisa. O operador da rampa não tem acesso a preços; o escritório não altera checklists de campo. Validação no servidor, não só na tela."
          />
          <FeatureCard
            className="md:col-span-4"
            icon={Camera}
            label="EVIDÊNCIA"
            title="Fotos de intake e entrega"
            text="Upload direto do celular ou tablet para armazenamento em nuvem. Cada foto vinculada à OS, com miniaturas na tela e galeria impressa no PDF da nota."
          />
          <FeatureCard
            className="md:col-span-4"
            icon={PenTool}
            label="SIGN-OFF"
            title="Assinatura digital"
            text="Piloto e operador assinam na tela ao finalizar. As assinaturas ficam gravadas na OS e reproduzidas na Nota de Serviço — validade documental sem papel."
          />
          <FeatureCard
            className="md:col-span-4"
            icon={Landmark}
            label="COMPLIANCE"
            title="Relatórios ANAC"
            text="Permanência de cada aeronave calculada automaticamente no servidor. Exporte CSV ou o PDF regulatório em paisagem, com a marca do hangar, pronto para envio."
          />
          <FeatureCard
            className="md:col-span-4"
            icon={BarChart3}
            label="BI: VENDAS"
            title="Inteligência de faturamento"
            text="Receita bruta, notas emitidas, ticket médio, receita por dia, top serviços, top clientes e top aeronaves — visão diária ou por período, com export CSV e PDF sintético."
          />
          <FeatureCard
            className="md:col-span-4"
            icon={FileText}
            label="DOCUMENTOS"
            title="PDFs com a sua marca"
            text="Configure logo, razão social, CNPJ, endereço e contato uma única vez. Notas de serviço, relatórios ANAC e de vendas saem com o cabeçalho da sua empresa."
          />
          <FeatureCard
            className="md:col-span-4"
            icon={Plane}
            label="CADASTROS"
            title="Dados mestres relacionais"
            text="Aeronaves vinculadas a clientes e pilotos padrão, catálogo de serviços com preço base. Autocomplete em toda a operação — cadastre uma vez, reutilize sempre."
          />
        </div>
      </div>
    </section>
  );
}

/* ------------------------------ POR QUE IMPORTA --------------------------- */
const BENEFITS = [
  {
    title: "Zero serviço não faturado",
    text: "Todo serviço executado nasce dentro da OS. Se foi feito na rampa, está na nota. A máquina de estados impede que uma OS finalizada escape do faturamento.",
  },
  {
    title: "Conformidade ANAC sem esforço",
    text: "O relatório de operações que levava dias para compilar sai em um clique, com permanências calculadas pelo sistema — não por memória.",
  },
  {
    title: "Trilha de auditoria completa",
    text: "Quem abriu a OS, quem lançou o serviço, quem assinou, quem precificou e quando. Cada registro tem autor e horário. Disputas com clientes se resolvem com evidência.",
  },
  {
    title: "Operação contínua no pátio",
    text: "Sem depender de sinal de internet na rampa. A equipe registra tudo no momento em que acontece — a base de qualquer dado confiável.",
  },
];

function Why() {
  return (
    <section className="relative py-24 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-sky-300 mb-4">Por que importa</div>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter text-white leading-[1.05]">
              O impacto direto no resultado da sua empresa.
            </h2>
            <p className="mt-6 text-slate-400 text-base leading-relaxed">
              Hangares que operam com rastreabilidade total faturam o que executam,
              respondem à ANAC em minutos e defendem cada centavo cobrado com evidência.
            </p>
            <div className="mt-8 rounded-sm overflow-hidden border border-white/10">
              <img
                src="/aerogestor-brand-art.jpg"
                alt="AeroGestor — logomarca metálica"
                className="w-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-x-10 gap-y-10">
            {BENEFITS.map(({ title, text }, i) => (
              <div key={title} className="border-t-2 border-sky-300/40 pt-5">
                <div className="font-mono text-xs text-slate-500 mb-2">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="font-display font-black text-xl tracking-tight text-white mb-3 flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-sky-300 mt-1 shrink-0" strokeWidth={1.5} />
                  {title}
                </h3>
                <p className="text-sm text-slate-400 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* --------------------------------- PERFIS --------------------------------- */
const PROFILES = [
  {
    role: "OPERADOR",
    title: "Na rampa",
    items: [
      "Abre a OS no pouso com checklist e fotos",
      "Lança serviços executados (sem ver preços)",
      "Registra movimentações de pátio",
      "Coleta a assinatura do piloto na saída",
      "Trabalha offline quando não há sinal",
    ],
  },
  {
    role: "ESCRITÓRIO",
    title: "No back-office",
    items: [
      "Revisa OSs aguardando faturamento",
      "Ajusta preços e aplica condições comerciais",
      "Emite a Nota de Serviço com numeração sequencial",
      "Gerencia cadastros de clientes, aeronaves e serviços",
      "Exporta relatórios ANAC e de vendas",
    ],
  },
  {
    role: "GESTOR / ADMIN",
    title: "Na direção",
    items: [
      "Acompanha KPIs de receita e operação",
      "Controla usuários e perfis de acesso",
      "Configura a marca do hangar nos documentos",
      "Audita o histórico completo de cada OS",
      "Convida novos usuários com um link seguro",
    ],
  },
];

function Profiles() {
  return (
    <section id="perfis" className="relative py-24 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-sky-300 mb-4 flex items-center gap-2">
          <Users className="w-3.5 h-3.5" /> Perfis de acesso
        </div>
        <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter text-white leading-[1.05] max-w-2xl">
          Cada equipe com a sua visão. Nenhum dado fora do lugar.
        </h2>
        <div className="mt-14 grid md:grid-cols-3 gap-4">
          {PROFILES.map(({ role, title, items }) => (
            <div key={role} className="rounded-sm border border-white/10 bg-[#132A42]/60 backdrop-blur-xl p-7">
              <span className="font-mono text-[9px] tracking-[0.22em] text-sky-300 border border-sky-300/30 rounded-sm px-2 py-1">
                RBAC: {role}
              </span>
              <h3 className="font-display font-black text-2xl tracking-tight text-white mt-5 mb-5">{title}</h3>
              <ul className="space-y-3">
                {items.map((it) => (
                  <li key={it} className="flex items-start gap-2.5 text-sm text-slate-400 leading-relaxed">
                    <span className="w-1 h-1 rounded-full bg-sky-300 mt-2 shrink-0" />
                    {it}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------- GUIA RÁPIDO DA OPERAÇÃO ------------------------ */
const STATUS_CARDS = [
  {
    color: "bg-emerald-400",
    border: "border-emerald-400/40",
    label: "VERDE",
    state: "Em Operação",
    text: "OS aberta no pouso. Realize o checklist de recebimento e as fotos agora.",
  },
  {
    color: "bg-amber-400",
    border: "border-amber-400/40",
    label: "AMARELO",
    state: "Em Andamento",
    text: "Serviços e movimentações sendo registrados na hora em que acontecem.",
  },
  {
    color: "bg-sky-400",
    border: "border-sky-400/40",
    label: "AZUL",
    state: "Aguardando Faturamento",
    text: "Finalizado com assinaturas e fotos finais. Edição bloqueada — pronto para o escritório.",
  },
];

const GOLDEN_RULES = [
  {
    title: "Acesso único e pessoal",
    text: "Nunca utilize o login de um colega. O sistema usa o seu login individual para rastreabilidade de segurança — cada registro tem autor.",
  },
  {
    title: "Checklist é respaldo, não burocracia",
    text: "A inspeção visual obrigatória — para-brisa, exterior, pneus, luzes, combustível — é a sua defesa técnica contra reclamações indevidas.",
  },
  {
    title: "Evidência visual imediata",
    text: "Encontrou irregularidade? Marque, descreva e anexe fotos do recebimento na hora. Um corte no pneu documentado no intake não vira disputa na entrega.",
  },
  {
    title: "A pista executa, o desktop gerencia",
    text: "O operador foca na execução impecável — sem ver preços. O sistema cruza os serviços registrados com as tabelas de preço e o escritório fatura.",
  },
];

function OperationGuide() {
  return (
    <section id="guia" className="relative py-24 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-sky-300 mb-4">Na prática</div>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter text-white leading-[1.05]">
              Dominando o AeroGestor na pista.
            </h2>
            <p className="mt-5 text-slate-400 text-base leading-relaxed">
              O operador é o coração da operação: a interação dele com o tablet garante que a
              aeronave seja cuidada — e a empresa, remunerada. O fim do papel começa na rampa,
              e a qualidade do faturamento começa na precisão do registro em campo.
            </p>
            <a
              href="/guia-operador-aerogestor.pdf"
              download="Guia-do-Operador-AeroGestor.pdf"
              data-testid="landing-download-guia"
            >
              <Button className="mt-7 h-auto px-6 py-3.5 rounded-sm bg-[#0052CC] hover:bg-[#003D99] text-white border border-white/20 font-bold text-sm tracking-wide">
                <Download className="w-4 h-4 mr-2" /> Baixar o Guia do Operador (PDF)
              </Button>
            </a>
          </div>
          <div className="lg:col-span-6">
            <div className="rounded-sm overflow-hidden border border-white/10 shadow-2xl shadow-black/40">
              <img
                src="/demo-capa-guia.jpg"
                alt="Dominando o AeroGestor na Pista — O Guia Prático do Operador de Hangar"
                className="w-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>

        {/* Fluxo de estados por cor */}
        <div className="mt-12 rounded-sm border border-white/10 overflow-hidden">
          <img
            src="/demo-estados.jpg"
            alt="Fluxo de estados da OS: Verde (Em Operação), Amarelo (Em Andamento), Azul (Aguardando Faturamento)"
            className="w-full object-cover"
            loading="lazy"
          />
        </div>
        <div className="mt-4 grid md:grid-cols-3 gap-4">
          {STATUS_CARDS.map(({ color, border, label, state, text }) => (
            <div key={label} className={`rounded-sm border ${border} bg-[#132A42]/60 backdrop-blur-xl p-6`}>
              <div className="flex items-center gap-2.5 mb-3">
                <span className={`w-2.5 h-2.5 rounded-full ${color}`} />
                <span className="font-mono text-[10px] tracking-[0.22em] text-slate-300">{label}</span>
              </div>
              <h3 className="font-display font-black text-xl tracking-tight text-white mb-2">{state}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{text}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 grid lg:grid-cols-12 gap-10 items-start">
          {/* Tabela de funções por perfil */}
          <div className="lg:col-span-6">
            <h3 className="font-display font-black text-xl tracking-tight text-white mb-5">Funções por perfil</h3>
            <div className="rounded-sm border border-white/10 overflow-hidden" data-testid="landing-role-matrix">
              <img
                src="/demo-perfis.jpg"
                alt="Tabela de funções por perfil: Administrador, Escritório e Operador"
                className="w-full object-cover"
                loading="lazy"
              />
            </div>
            <p className="mt-4 text-sm text-slate-400 leading-relaxed">
              O operador foca na execução real — sem preocupação com preços, apenas no
              controle impecável da pista.
            </p>
          </div>

          {/* Regras de ouro */}
          <div className="lg:col-span-6">
            <h3 className="font-display font-black text-xl tracking-tight text-white mb-5">Regras de ouro da operação</h3>
            <div className="space-y-4">
              {GOLDEN_RULES.map(({ title, text }, i) => (
                <div key={title} className="rounded-sm border border-white/10 bg-[#132A42]/60 backdrop-blur-xl p-5 flex gap-4">
                  <span className="font-mono font-bold text-2xl text-sky-300/50 leading-none mt-0.5">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <h4 className="font-display font-black text-base tracking-tight text-white mb-1">{title}</h4>
                    <p className="text-sm text-slate-400 leading-relaxed">{text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sincronização pista ↔ escritório */}
        <div className="mt-12 rounded-sm border border-white/10 overflow-hidden relative">
          <img
            src="/demo-sincronizacao.jpg"
            alt="Sincronização instantânea entre o tablet na pista e o painel gerencial no escritório"
            className="w-full object-cover"
            loading="lazy"
          />
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------- CTA ----------------------------------- */
function FinalCta() {
  return (
    <section id="contato" className="relative py-24 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="relative rounded-sm border border-white/10 overflow-hidden">
          <img
            src="/aerogestor-brand-art.jpg"
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-center"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1726] via-[#0A1726]/90 to-[#0A1726]/30" />
          <div className="relative z-10 px-8 py-16 sm:px-14 sm:py-20 max-w-2xl">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-sky-300 mb-4">
              Sua operação, agora digital
            </div>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter text-white leading-[1.05]">
              Esteja pronto para o próximo pouso.
            </h2>
            <p className="mt-5 text-slate-300 text-base leading-relaxed">
              O AeroGestor profissionaliza o trabalho da sua equipe, elimina o retrabalho
              de papel e eleva o padrão de atendimento do hangar. Acesse com suas
              credenciais corporativas ou fale com a nossa equipe.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Link to="/login" data-testid="landing-final-cta-login">
                <Button className="h-13 px-8 py-4 h-auto rounded-sm bg-[#0052CC] hover:bg-[#003D99] text-white border border-white/20 font-bold text-sm tracking-wide">
                  Acessar o sistema <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <a href={DEMO_MAILTO} data-testid="landing-final-cta-demo">
                <Button variant="outline" className="h-13 px-8 py-4 h-auto rounded-sm bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-sky-300 font-bold text-sm tracking-wide">
                  <Mail className="w-4 h-4 mr-2" /> Solicitar demonstração
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* --------------------------------- FOOTER ---------------------------------- */
function Footer() {
  return (
    <footer className="border-t border-white/10 py-10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
        <div className="flex items-center gap-3">
          <img src="/aerogestor-symbol.jpg" alt="AeroGestor" className="w-8 h-8 rounded-sm object-cover ring-1 ring-white/20" />
          <div>
            <div className="font-display font-black text-lg tracking-tight ag-brand-metallic">AeroGestor</div>
            <div className="text-[9px] tracking-[0.25em] uppercase text-slate-500">Hangar Operating System</div>
          </div>
        </div>
        <div className="flex flex-col sm:items-end gap-1.5 text-sm text-slate-500">
          <a href="mailto:brito@service-engenharia.com" className="hover:text-sky-300 transition-colors font-mono text-xs" data-testid="landing-footer-email">
            brito@service-engenharia.com
          </a>
          <a
            href="/guia-operador-aerogestor.pdf"
            download="Guia-do-Operador-AeroGestor.pdf"
            className="hover:text-sky-300 transition-colors font-mono text-xs"
            data-testid="landing-footer-guia"
          >
            Guia do Operador (PDF)
          </a>
          <span className="font-mono text-[10px] tracking-[0.15em]">AEROGESTOR © 2026 — TODOS OS DIREITOS RESERVADOS</span>
        </div>
      </div>
    </footer>
  );
}

/* --------------------------------- PAGE ----------------------------------- */
export default function Landing() {
  return (
    <div className="min-h-screen ag-metal-bg text-white" data-testid="landing-page">
      <Nav />
      <Hero />
      <Problem />
      <Flow />
      <Features />
      <Why />
      <Profiles />
      <OperationGuide />
      <FinalCta />
      <Footer />
    </div>
  );
}
